import { Fragment, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { api, idr } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import { useDebounce } from "../../hooks/useDebounce";
import { useModalGuard, UnsavedGuardBar } from "../../hooks/useModalGuard";
import { useCabangOptions } from "../../lib/cabang";
import { DatePicker } from "../../components/ui/date-picker";
import LoadingState from "../../components/ui/loading-state";
import { TabelDesktop, DaftarKartuMobile, KartuBaris, KartuBarisHeader, KartuBarisMeta } from "../../components/ui/responsive-table";
import { hariIniISO, formatTanggal, dateDariIso } from "../../lib/tanggal";
import InputRupiah from "../../components/InputRupiah";
import AttachmentPanel from "./AttachmentPanel";
import { useSortState, sortRows, SortTh } from "../../components/ui/sort";
import { BatasDaftarNotice } from "./_shared";
import PanelImporAktivasi from "./pp/PanelImporAktivasi";
import PindahUnitModal from "./pp/PindahUnitModal";
import PanelSelesai from "./pp/PanelSelesai";
import DashboardProgramSelesai from "./pp/DashboardProgramSelesai";
import PanelPengaturanPP from "./pp/PanelPengaturanPP";
import TetapkanJenisModal from "./pp/TetapkanJenisModal";
import TabelSkuProgram from "./pp/TabelSkuProgram";
import SelKolom, { kelasRata, rataKolom } from "./pp/SelKolom";
import {
  HALAMAN_PP, HALAMAN_SEMUA, JENIS, LABEL_JENIS, LABEL_KANAL, MODE, KOLOM_UNIT, HALAMAN_BERBAGIAN, BAGIAN, LABEL_BAGIAN,
  halamanDariKunci, istilahNominal, modeProgram, kunciTampilan, labelProgram,
  aksesUrut, itemKosong, formProgramValid, bangunBodyProgram,
} from "./pp/halamanPP";

// `GET /cashback/programs` mengurut created_at desc lalu memotong
// `to_list(1000)` di backend TANPA parameter urut dari klien (lihat
// cashback_claim.py) — sort di tabel ini murni client-side.
const CB_PROGRAM_BATAS_SERVER = 1000;
import {
  Plus, Percent, Search, RotateCcw, X, ChevronRight, ListFilter, LayoutDashboard,
  AlertTriangle, CheckCircle2, Clock, Send, Ban, Wallet, Trash2, Pencil,
  Lock, Paperclip, PlusCircle, CalendarRange, PackageSearch, FileSpreadsheet, ArrowRightLeft,
  History, Settings2, Tags,
} from "lucide-react";

// KENAPA path literal terpisah: router `cashback_claim.py` di backend memakai
// path literal ("/cashback/programs", dst) dan dipasang `prefix="/api"` SAJA —
// pola SAMA dengan `credit_note.py` (lihat `CN_BASE` di FinanceCreditNote.jsx),
// BUKAN di bawah agregator "/finance". Kalau prefix di server.py diubah, cukup
// ubah SATU baris ini.
export const CB_BASE = "/cashback";

// ============ Helper unduh berkas biner (template Excel, dst) ============
// Pola SAMA dengan `_downloadBlob` di FinanceLaporan.jsx — diduplikasi kecil
// di sini (bukan diimpor lintas halaman) karena keduanya berdiri sendiri;
// diekspor supaya CashbackClaim.jsx (ekspor xlsx/pdf) memakai SATU sumber
// yang sama dalam keluarga berkas cashback ini.
export async function downloadBlob(url, filename, mime, params) {
  const res = await api.get(url, { responseType: "blob", params });
  const blob = new Blob([res.data], { type: mime });
  const objUrl = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(objUrl);
}

// ============ Status & pill bersama (dipakai CashbackClaim.jsx + CashbackClaimDetail.jsx) ============
// Status PROGRAM — nilainya SAMA dengan `PROGRAM_STATUS_*` di backend
// (`cashback_claim.py`). Satu-satunya tempat teks status program diketik di
// layar ini: brief 22 Sep (A9) menambah status "Selesai", dan penjaga yang
// menulis "aktif" sebagai teks di tempat lain akan diam-diam salah begitu
// status itu lahir. Dijaga `backend/tests/test_cashback_pp0.py`.
export const PROGRAM_STATUS = Object.freeze({
  AKTIF: "aktif",
  DITUTUP: "ditutup",
  // Brief 22 Sep A9 (PP4): dinyatakan Selesai oleh PIC — terkunci, angkanya dari snapshot.
  SELESAI: "selesai",
});
// Satu peta label; `STATUS_OPTIONS` di bawah diturunkan dari sini.
const PROGRAM_STATUS_META = {
  [PROGRAM_STATUS.AKTIF]:   { label: "Aktif",   cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  [PROGRAM_STATUS.DITUTUP]: { label: "Ditutup", cls: "bg-slate-100 text-slate-600 border-slate-200" },
  [PROGRAM_STATUS.SELESAI]: { label: "Selesai", cls: "bg-indigo-50 text-indigo-700 border-indigo-200" },
};
// `mode_mesin` program jenis baru (Support Aktivasi / Support Cashback) — nilai
// SAMA dengan `MODE_KEANGGOTAAN` di backend `routers/pp/aturan.py`; satu sumber
// di peta definisi halaman (`pp/halamanPP.js`, dibandingkan tes dengan backend).
const MODE_KEANGGOTAAN = MODE.KEANGGOTAAN;
export function ProgramStatusPill({ status }) {
  const m = PROGRAM_STATUS_META[status];
  // Status yang belum dikenal layar ini ditampilkan APA ADANYA, bukan jatuh ke
  // "Aktif" seperti dulu — program yang sudah Selesai/ditutup tidak boleh
  // terbaca masih berjalan hanya karena layarnya lebih tua dari datanya.
  const label = m ? m.label : (status || "-");
  const cls = m ? m.cls : "bg-amber-50 text-amber-800 border-amber-200";
  return <span className={`inline-block text-[11px] px-2 py-0.5 rounded border font-medium whitespace-nowrap ${cls}`}>{label}</span>;
}

export const CLAIM_STATUS_META = {
  draft:               { label: "Draft",               cls: "bg-slate-100 text-slate-600 border-slate-200" },
  submitted:           { label: "Disubmit ke Supplier", cls: "bg-sky-50 text-sky-700 border-sky-200" },
  diputuskan:          { label: "Diputuskan",           cls: "bg-amber-50 text-amber-700 border-amber-200" },
  settlement_sebagian: { label: "Settlement Sebagian",  cls: "bg-orange-50 text-orange-700 border-orange-200" },
  settled:             { label: "Settled (Lunas)",      cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  closed:              { label: "Closed",               cls: "bg-slate-800 text-white border-slate-800" },
  dibatalkan:          { label: "Dibatalkan",           cls: "bg-rose-50 text-rose-700 border-rose-200" },
};
export function ClaimStatusPill({ status }) {
  const m = CLAIM_STATUS_META[status] || CLAIM_STATUS_META.draft;
  return <span className={`inline-block text-[11px] px-2 py-0.5 rounded border font-medium whitespace-nowrap ${m.cls}`}>{m.label}</span>;
}

const ITEM_STATUS_META = {
  draft:       { label: "Draft",      cls: "bg-slate-100 text-slate-600 border-slate-200" },
  submitted:   { label: "Disubmit",   cls: "bg-sky-50 text-sky-700 border-sky-200" },
  approved:    { label: "Disetujui",  cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  rejected:    { label: "Ditolak",    cls: "bg-rose-50 text-rose-700 border-rose-200" },
  received:    { label: "Diterima",  cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  dibatalkan:  { label: "Dibatalkan", cls: "bg-slate-100 text-slate-500 border-slate-200" },
};
export function ItemStatusPill({ status }) {
  const m = ITEM_STATUS_META[status] || ITEM_STATUS_META.draft;
  return <span className={`inline-block text-[11px] px-2 py-0.5 rounded border font-medium whitespace-nowrap ${m.cls}`}>{m.label}</span>;
}

const ELIGIBILITY_META = {
  eligible:           { label: "Eligible",         cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  pending_activation: { label: "Menunggu Aktivasi", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  not_eligible:       { label: "Tidak Eligible",   cls: "bg-slate-100 text-slate-600 border-slate-200" },
  already_claimed:    { label: "Sudah Diklaim",    cls: "bg-sky-50 text-sky-700 border-sky-200" },
};
export function EligibilityPill({ status }) {
  const m = ELIGIBILITY_META[status] || ELIGIBILITY_META.not_eligible;
  return <span className={`inline-block text-[11px] px-2 py-0.5 rounded border font-medium whitespace-nowrap ${m.cls}`}>{m.label}</span>;
}

/** Penanda urgensi deadline submit (brief butir 23) — murni perbandingan
 *  TANGGAL kalender (bukan penghitungan uang), jadi aman dihitung di klien.
 *  `submittedAfterDeadline` datang APA ADANYA dari backend (`batch.
 *  submitted_after_deadline`), tidak pernah ditebak ulang di sini. */
export function DeadlineBadge({ deadline, statusBatch, submittedAfterDeadline }) {
  if (!deadline) return null;
  if (submittedAfterDeadline) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded border font-medium bg-rose-600 text-white border-rose-600 whitespace-nowrap">
        <AlertTriangle className="w-3 h-3" /> Disubmit lewat deadline
      </span>
    );
  }
  if (statusBatch && statusBatch !== "draft") {
    return <span className="text-[11px] text-slate-500">Deadline {formatTanggal(deadline)}</span>;
  }
  const hariIni = dateDariIso(hariIniISO());
  const batas = dateDariIso(deadline);
  if (!hariIni || !batas) return null;
  const hariSisa = Math.round((batas - hariIni) / 86400000);
  if (hariSisa < 0) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded border font-medium bg-rose-600 text-white border-rose-600 whitespace-nowrap">
        <AlertTriangle className="w-3 h-3" /> OVERDUE {Math.abs(hariSisa)} hari — berisiko hangus
      </span>
    );
  }
  if (hariSisa <= 3) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded border font-medium bg-amber-100 text-amber-800 border-amber-300 whitespace-nowrap">
        <Clock className="w-3 h-3" /> H-{hariSisa} ({formatTanggal(deadline)})
      </span>
    );
  }
  return <span className="text-[11px] text-slate-500">Deadline {formatTanggal(deadline)}</span>;
}

/** Penanda "klaim dibuat di luar periode klaim program" (permintaan bos 21 Agu
 *  E1: JANGAN ditolak — beri peringatan merah, biarkan tersimpan). Pola SAMA
 *  dengan `DeadlineBadge` di atas: `diLuarPeriodeKlaim` datang APA ADANYA dari
 *  backend (`batch.di_luar_periode_klaim`), tidak pernah ditebak ulang di
 *  sini — beda gerbang dari periode AKTIVASI (yang tetap menolak 400). */
export function PeriodeKlaimBadge({ diLuarPeriodeKlaim, alasan }) {
  if (!diLuarPeriodeKlaim) return null;
  return (
    <span title={alasan || ""}
          className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded border font-medium bg-rose-600 text-white border-rose-600 whitespace-nowrap">
      <AlertTriangle className="w-3 h-3" /> Di luar periode klaim
    </span>
  );
}

// ============ Modal & Field generik (pola sama dengan FinanceCreditNote.jsx) ============
export const inputCls = "w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200";

export function Field({ label, hint, children, col2 }) {
  return (
    <div className={col2 ? "col-span-2" : ""}>
      <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
      {children}
      {hint && <p className="text-[11px] text-slate-500 mt-1">{hint}</p>}
    </div>
  );
}

// `dangerous` opsional (default false = tutup langsung, perilaku sama seperti
// sebelumnya) — pasang HANYA pada modal yang formnya panjang atau menyentuh
// nominal yang diketik staf sendiri. Lihat docstring useModalGuard.jsx untuk
// alasan lengkap; di berkas ini yang ditandai cuma Program Price Protection
// (daftar SKU + harga lama/baru, lihat titik panggilnya di bawah).
export function Modal({ title, icon: Icon, tone, children, onClose, onSubmit, submitLabel, busy, disabled, testid, wide, dangerous }) {
  const guard = useModalGuard({ onClose, onSubmit, busy, disabled, dangerous });
  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={guard.requestClose}>
      <div ref={guard.contentRef} onClick={(e) => e.stopPropagation()} data-testid={testid}
           className={`bg-white rounded-2xl w-full ${wide ? "max-w-3xl" : "max-w-lg"} max-h-[88vh] flex flex-col shadow-2xl`}>
        <div className="p-5 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-display font-bold text-lg flex items-center gap-2">
            {Icon && <Icon className={`w-5 h-5 ${tone === "rose" ? "text-rose-600" : "text-emerald-600"}`} />} {title}
          </h3>
          <button onClick={guard.requestClose} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 overflow-y-auto flex-1 space-y-3">{children}</div>
        {guard.confirming && (
          <UnsavedGuardBar onStay={guard.cancelClose} onDiscard={guard.confirmDiscard} testid={testid ? `${testid}-guard` : "modal-guard-bar"} />
        )}
        {onSubmit && (
          <div className="p-4 border-t border-slate-100 flex items-center justify-end gap-2">
            <button onClick={guard.requestClose} className="h-10 px-4 rounded-lg border border-slate-200 text-sm text-slate-700 hover:bg-slate-50">Batal</button>
            <button onClick={onSubmit} disabled={busy || disabled} data-testid={`${testid}-submit`}
                    className={`h-10 px-4 rounded-lg text-white text-sm font-medium disabled:opacity-50 ${tone === "rose" ? "bg-rose-600 hover:bg-rose-700" : "bg-emerald-600 hover:bg-emerald-700"}`}>
              {busy ? "Memproses..." : submitLabel}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export function ReasonModal({ title, icon, label, submitLabel, onClose, onSubmit, testid }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!reason.trim()) return toast.error("Alasan wajib diisi");
    setBusy(true);
    try { await onSubmit(reason.trim()); } finally { setBusy(false); }
  };
  return (
    <Modal title={title} icon={icon} tone="rose" onClose={onClose} onSubmit={submit} submitLabel={submitLabel} busy={busy} disabled={!reason.trim()} testid={testid}>
      <Field label={label}>
        <textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} data-testid={`${testid}-input`} className={inputCls + " h-auto py-2"} />
      </Field>
    </Modal>
  );
}

// ============ Halaman utama ============
const STATUS_OPTIONS = [
  { value: "", label: "Semua Status" },
  ...Object.entries(PROGRAM_STATUS_META).map(([value, m]) => ({ value, label: m.label })),
];

// Tampilan halaman yang boleh dibuka lewat `?view=` — dipakai tautan Pusat
// Laporan (registry `beli.cashback-periode` -> `?view=laporan`). Nilai lain atau
// kosong = dashboard, sama seperti sebelum parameter ini ada. "lama" & "pengaturan"
// (PP2) = tab tetap di SETIAP halaman: di menu 3 tingkat halaman "Semua Program"
// tidak punya butir menu, jadi program lama & Pengaturan tidak boleh hanya bisa
// dicapai lewat banner (keberatan jalur-layar-sisa).
const VIEW_HALAMAN = ["dashboard", "list", "laporan", "lama", "pengaturan"];

const EMPTY_FORM = {
  nama_program: "", supplier_nama: "", brand: "", cabang: "",
  items: [itemKosong(null)],
  tanggal_price_drop: hariIniISO(), periode_aktivasi_dari: "", periode_aktivasi_sampai: "",
  periode_klaim_dari: "", periode_klaim_sampai: "", deadline_submit: "",
  metode_settlement_default: "", keterangan: "",
};

const METODE_LABEL = {
  credit_note: "Credit Note (CN)",
  kas_bank: "Kas/Bank Langsung",
  potong_utang_langsung: "Potong Utang Langsung",
};

const SUBJUDUL_HALAMAN = {
  [JENIS.AKTIVASI]: "Nominal Support Aktivasi tetap per SKU untuk unit yang diaktivasi dalam periode program.",
  [JENIS.CASHBACK]: "Nominal Cashback = Harga Lama (STP PROMO) − Harga Baru, per unit yang terjual/diaktivasi dalam periode program.",
};

// Empat halaman (brief 22 Sep A1–A5) dari SATU komponen berparameter. Tanpa
// `halamanTetap` (rute lama `/finance/cashback/programs`), halaman dipilih lewat
// pemilih di bawah judul dan disimpan di `?halaman=` — menu sidebar tidak
// berubah. Dengan `halamanTetap` (menu 3 tingkat, rute per halaman), pemilihnya
// disembunyikan: satu komponen, dua cara masuk.
export default function CashbackProgram({ halamanTetap = null } = {}) {
  const { can } = useAuth();
  const navigate = useNavigate();
  const cabangOptions = useCabangOptions({ denganKosong: true });

  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const kunciHalaman = halamanTetap || searchParams.get("halaman") || HALAMAN_SEMUA;
  // Nilai asing / "semua" = halaman Semua Program (perilaku sebelum PP2).
  const halaman = halamanDariKunci(kunciHalaman);
  const pilihHalaman = (kunci) => {
    setSearchParams((sp) => {
      const n = new URLSearchParams(sp);
      if (kunci === HALAMAN_SEMUA) n.delete("halaman"); else n.set("halaman", kunci);
      return n;
    }, { replace: true });
  };
  // Dibaca SEKALI saat halaman dibuka; pindah tampilan sesudahnya tetap lewat
  // tombol di atas (tidak menulis balik ke URL).
  const [view, setView] = useState(() => {
    const v = new URLSearchParams(location.search).get("view");
    return VIEW_HALAMAN.includes(v) ? v : "dashboard";
  }); // "dashboard" | "list" | "laporan" | "lama" | "pengaturan"
  const [reload, setReload] = useState(0);
  const bump = () => setReload((n) => n + 1);

  const canCreate = can("finance_cashback_claim", "create");

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detailProgram, setDetailProgram] = useState(null);

  const tombolView = [
    { key: "dashboard", label: "Dashboard", icon: LayoutDashboard, tid: "cb-prog-view-dashboard" },
    { key: "list", label: "Daftar Program", icon: ListFilter, tid: "cb-prog-view-daftar" },
    { key: "laporan", label: "Laporan Periode", icon: CalendarRange, tid: "cb-prog-view-laporan" },
    { key: "lama", label: "Program Lama", icon: History, tid: "cb-prog-view-lama" },
    { key: "pengaturan", label: "Pengaturan", icon: Settings2, tid: "cb-prog-view-pengaturan" },
  ];

  return (
    <div className="p-4 md:p-8 space-y-6" data-testid="finance-cashback-program">
      <div className="flex items-start justify-between flex-col lg:flex-row gap-3">
        <div>
          <p className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold">
            {halaman ? `Program ${LABEL_JENIS[halaman.jenis]}` : "Hak Cashback & Support dari Supplier"}
          </p>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 mt-1 flex items-center gap-2" data-testid="cb-pp-judul">
            <Percent className="w-7 h-7 text-emerald-600" /> {halaman ? halaman.label : "Program Price Protection"}
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            {halaman ? SUBJUDUL_HALAMAN[halaman.jenis]
              : "Semua program. Program lama dihitung dari modal ASLI tiap unit; program jenis baru per Support Aktivasi/Cashback × ZOA GROUP/Dealer Partner."}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="bg-white border border-slate-200 rounded-lg p-1 inline-flex flex-wrap">
            {tombolView.map((t) => (
              <button key={t.key} onClick={() => setView(t.key)} data-testid={t.tid}
                      className={`h-8 px-3 rounded-md text-xs font-medium inline-flex items-center gap-1.5 transition ${view === t.key ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"}`}>
                <t.icon className="w-3.5 h-3.5" /> {t.label}
              </button>
            ))}
          </div>
          {canCreate && view === "list" && (
            <button onClick={() => setShowForm(true)} data-testid="cb-prog-new"
                    className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium inline-flex items-center gap-2">
              <Plus className="w-4 h-4" /> Program Baru
            </button>
          )}
        </div>
      </div>

      {/* Pemilih halaman (menu sidebar tidak berubah — keputusan Bos: menu baru
          dicoba dulu). Disembunyikan kalau halaman sudah ditetapkan rutenya. */}
      {!halamanTetap && (
        <div className="flex items-center gap-1.5 flex-wrap" data-testid="cb-pp-pemilih-halaman">
          {[{ kunci: HALAMAN_SEMUA, label: "Semua Program" }, ...HALAMAN_PP].map((h) => (
            <button key={h.kunci} onClick={() => pilihHalaman(h.kunci)} data-testid={`cb-pp-halaman-${h.kunci}`}
                    className={`h-9 px-3 rounded-lg border text-xs font-medium ${(halaman ? halaman.kunci : HALAMAN_SEMUA) === h.kunci ? "bg-emerald-600 border-emerald-600 text-white" : "border-slate-200 text-slate-600 bg-white hover:bg-slate-50"}`}>
              {h.label}
            </button>
          ))}
        </div>
      )}

      {view === "dashboard" ? (
        <CashbackDashboard key={`dash-${kunciHalaman}`} reloadKey={reload} halaman={halaman} />
      ) : view === "laporan" ? (
        <LaporanPeriode key={`lap-${kunciHalaman}`} halaman={halaman} />
      ) : view === "pengaturan" ? (
        <PanelPengaturanPP cbBase={CB_BASE} />
      ) : view === "lama" ? (
        // Tinjauan PP r1: seluruh program produksi hari ini adalah program LAMA.
        // Di menu 3 tingkat halaman "Semua Program" tidak punya butir menu, jadi
        // funnel, hangus, dan recovery program lama harus bisa dibaca dari tab
        // ini di SETIAP halaman — bukan hanya lewat tautan notifikasi.
        <div className="space-y-6" data-testid="cb-prog-lama-view">
          <CashbackDashboard key="dash-lama" reloadKey={reload} mode={MODE.LAMA} />
          <DaftarProgram key={`lama-${kunciHalaman}`} halaman={halaman} lama reloadKey={reload}
                         onBuka={setDetailProgram} onLihatLama={() => setView("lama")} />
        </div>
      ) : (
        <DaftarProgram key={`${view}-${kunciHalaman}`} halaman={halaman} lama={view === "lama"} reloadKey={reload}
                       onBuka={setDetailProgram} onLihatLama={() => setView("lama")} />
      )}

      {showForm && (
        <FormProgram cabangOptions={cabangOptions} jenisBaru={halaman ? halaman.jenis : null}
                     kanalBaru={halaman ? halaman.kanal : null} onClose={() => setShowForm(false)}
                     onCreated={() => { setShowForm(false); bump(); }} />
      )}
      {editing && (
        <FormProgram program={editing} cabangOptions={cabangOptions} onClose={() => setEditing(null)}
                     onCreated={(updated) => { setEditing(null); bump(); setDetailProgram((d) => d && d.id === updated.id ? updated : d); }} />
      )}
      {detailProgram && (
        <ProgramDetailModal program={detailProgram} onClose={() => setDetailProgram(null)}
                            onChanged={(updated) => { bump(); setDetailProgram(updated); }}
                            onDeleted={() => { setDetailProgram(null); bump(); }}
                            onEdit={() => { setEditing(detailProgram); }}
                            navigate={navigate} />
      )}
    </div>
  );
}

// ============ Daftar program (per halaman / program lama) ============
// Total per program (brief hal. 6 & 9: "Sistem otomatis menghitung Total ...
// pada masing-masing program") dari `GET /cashback/pp/ringkasan` — hitungan
// SATU dengan panel Selesai; layar tidak menjumlah per unit. Dimuat per 100
// program (batas backend).
const RINGKASAN_PER_PERMINTAAN = 100;

function DaftarProgram({ halaman, lama, reloadKey, onBuka, onLihatLama }) {
  const [q, setQ] = useState("");
  const [statusF, setStatusF] = useState("");
  const [supplierF, setSupplierF] = useState("");
  const qDeb = useDebounce(q, 400);
  const supplierDeb = useDebounce(supplierF, 400);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [muat, setMuat] = useState(0);
  const [ringkasan, setRingkasan] = useState({});
  const [lamaBelumDitetapkan, setLamaBelumDitetapkan] = useState(0);
  // Tab Program Lama ikut menampilkan total per program (tinjauan PP r1) —
  // hitungannya SATU dengan halaman jenis (`/cashback/pp/ringkasan`).
  const denganTotal = !!halaman || lama;
  // Program lama di halaman jenis ini (rancangan v1 §4.8): halaman Aktivasi
  // memuat program lama yang DITETAPKAN Support Aktivasi (mode Tandai — tanpa
  // kanal, jadi tidak lolos saringan kanal halaman), halaman Cashback memuat
  // program lama lainnya (rumus modal, semua kanal).
  const [programLama, setProgramLama] = useState([]);

  useEffect(() => {
    let batal = false;
    setLoading(true); setLoadError(null);
    (async () => {
      try {
        const params = {};
        if (qDeb) params.q = qDeb;
        if (statusF) params.status = statusF;
        if (supplierDeb) params.supplier = supplierDeb;
        if (lama) params.mode = MODE.LAMA;
        else if (halaman) { params.jenis = halaman.jenis; params.kanal = halaman.kanal; }
        const r = await api.get(`${CB_BASE}/programs`, { params });
        if (batal) return;
        setItems(r.data.items || []);
      } catch (e) {
        if (!batal) { setLoadError(e); toast.error(e?.response?.data?.detail || "Gagal memuat daftar Program Price Protection"); }
      } finally { if (!batal) setLoading(false); }
    })();
    return () => { batal = true; };
  }, [halaman, lama, qDeb, statusF, supplierDeb, reloadKey, muat]);

  useEffect(() => {
    if (!denganTotal || items.length === 0) { setRingkasan({}); return undefined; }
    let batal = false;
    (async () => {
      const hasil = {};
      for (let i = 0; i < items.length; i += RINGKASAN_PER_PERMINTAAN) {
        const ids = items.slice(i, i + RINGKASAN_PER_PERMINTAAN).map((p) => p.id).join(",");
        try {
          const r = await api.get(`${CB_BASE}/pp/ringkasan`, { params: { program_ids: ids } });
          Object.assign(hasil, r.data.items || {});
        } catch (e) { if (!batal) toast.error(e?.response?.data?.detail || "Gagal memuat total per program"); }
        if (batal) return;
        setRingkasan({ ...hasil });
      }
    })();
    return () => { batal = true; };
  }, [denganTotal, items]);

  // Banner program lama di halaman jenis (brief: dua program aktivasi produksi
  // dibuat lewat form cashback) — keputusan "belum ditetapkan" dari backend.
  useEffect(() => {
    if (!halaman || lama) return undefined;
    let batal = false;
    api.get(`${CB_BASE}/programs`, { params: { mode: MODE.LAMA } })
      .then((r) => {
        if (batal) return;
        const semua = r.data.items || [];
        setLamaBelumDitetapkan(semua.filter((p) => !p.jenis_efektif).length);
        setProgramLama(semua);
      })
      .catch(() => {});
    return () => { batal = true; };
  }, [halaman, lama]);

  const resetFilter = () => { setQ(""); setStatusF(""); setSupplierF(""); };
  const filterAktif = q || statusF || supplierF;
  // Tab Program Lama memakai kolom & istilah program lama di halaman mana pun.
  const aktivasi = !lama && halaman && halaman.jenis === JENIS.AKTIVASI;
  const istilah = halaman && !lama ? istilahNominal(halaman.jenis) : "Cashback";
  const lamaSeJenis = !halaman || lama ? [] : programLama.filter((p) => (halaman.jenis === JENIS.AKTIVASI
    ? p.jenis_efektif === JENIS.AKTIVASI : p.jenis_efektif !== JENIS.AKTIVASI));

  // Urutan bawaan (created_at desc dari backend) tetap sampai header diklik.
  const { sortKey, sortDir, toggle } = useSortState(null);
  const sortedItems = sortRows(items, sortKey, sortDir, {
    kode: (it) => it.code || "",
    program: (it) => it.nama_program || "",
    brand: (it) => [it.brand, it.cabang].filter(Boolean).join(" · "),
    sku: (it) => (it.items || []).length,
    total: (it) => ringkasan[it.id]?.total_hak?.total_cashback || 0,
    status: (it) => it.status || "",
  });
  const jumlahKolom = 8 + (denganTotal ? 1 : 0);

  // `akhiran` membedakan testid tabel desktop dan kartu HP (keduanya ada di DOM).
  const selTotal = (it, akhiran = "") => {
    const r = ringkasan[it.id];
    if (!r) return <span className="text-[11px] text-slate-400">…</span>;
    return (
      <div data-testid={`cb-prog-total${akhiran}-${it.id}`}>
        <p className="font-mono font-semibold text-emerald-700">{idr(r.total_hak.total_cashback)}</p>
        <p className="text-[10px] text-slate-500">{r.total_hak.count} unit eligible{r.sumber === "snapshot" ? " · dibekukan" : ""}</p>
        {r.hak_stok_belum_terjual.count > 0 && (
          <p className="text-[10px] text-amber-700">{r.hak_stok_belum_terjual.count} stok gudang · {idr(r.hak_stok_belum_terjual.total_cashback)}</p>
        )}
      </div>
    );
  };

  return (
    <>
      {lama && (
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-sm text-slate-700" data-testid="cb-prog-lama-info">
          <p className="font-semibold text-slate-800">Program lama (sebelum brief 22 Sep 2026)</p>
          <p className="mt-1">Dihitung dengan rumus lama (modal asli − harga baru), tanpa kanal. Selama jenisnya belum ditetapkan,
            klaimnya mengunci Support Aktivasi <b>dan</b> Support Cashback. Buka program lalu pakai “Jadikan Program Support Aktivasi”
            atau “Tetapkan: Support Cashback” di tab Info Program.</p>
        </div>
      )}
      {!lama && halaman && lamaBelumDitetapkan > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-900 flex items-center justify-between gap-3 flex-wrap" data-testid="cb-prog-lama-banner">
          <span><AlertTriangle className="w-4 h-4 inline mr-1" />{lamaBelumDitetapkan} program lama belum ditetapkan jenisnya — klaimnya mengunci Support Aktivasi dan Cashback sekaligus.</span>
          <button onClick={onLihatLama} data-testid="cb-prog-lama-banner-buka" className="text-xs font-medium text-amber-900 underline">Lihat Program Lama</button>
        </div>
      )}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 space-y-3">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari kode, nama program, atau supplier..."
                   data-testid="cb-prog-search" className="w-full h-10 pl-9 pr-3 border border-slate-200 rounded-lg text-sm focus:border-emerald-500 outline-none" />
          </div>
          <select value={statusF} onChange={(e) => setStatusF(e.target.value)} data-testid="cb-prog-filter-status"
                  className="h-10 px-3 border border-slate-200 rounded-lg text-sm min-w-[150px]">
            {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <input value={supplierF} onChange={(e) => setSupplierF(e.target.value)} placeholder="Filter supplier..." data-testid="cb-prog-filter-supplier"
                 className="h-10 px-3 border border-slate-200 rounded-lg text-sm min-w-[160px]" />
          {filterAktif && (
            <button onClick={resetFilter} data-testid="cb-prog-filter-reset"
                    className="h-10 px-3 rounded-lg text-slate-500 hover:text-slate-800 text-sm inline-flex items-center gap-1.5">
              <RotateCcw className="w-3.5 h-3.5" /> Reset
            </button>
          )}
        </div>
      </div>

      <BatasDaftarNotice shown={items.length} batas={CB_PROGRAM_BATAS_SERVER} />
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <TabelDesktop>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-100 text-[11px] uppercase tracking-wide text-slate-500">
              <tr>
                <SortTh label={aktivasi ? "Kode / Dibuat" : "Kode / Price Drop"} sortKeyName="kode" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="cb-prog-sort-kode" />
                <SortTh label="Program / Supplier" sortKeyName="program" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="cb-prog-sort-program" />
                <SortTh label={aktivasi ? "Produk / Cabang" : "Brand / Cabang"} sortKeyName="brand" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="cb-prog-sort-brand" />
                <SortTh label="SKU" sortKeyName="sku" sortKey={sortKey} sortDir={sortDir} onSort={toggle} align="right" testid="cb-prog-sort-sku" />
                <th className="px-3 py-2.5 text-left">Periode Aktivasi</th>
                <th className="px-3 py-2.5 text-left">{aktivasi ? "Deadline Aktivasi" : "Deadline Submit"}</th>
                {denganTotal && (
                  <SortTh label={`Total ${istilah}`} sortKeyName="total" sortKey={sortKey} sortDir={sortDir} onSort={toggle} align="right" testid="cb-prog-sort-total" />
                )}
                <SortTh label="Status" sortKeyName="status" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="cb-prog-sort-status" />
                <th className="px-3 py-2.5 text-right">Aksi</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              <LoadingState loading={loading} error={loadError} onRetry={() => setMuat((n) => n + 1)} kosong={!loading && !loadError && items.length === 0}
                            pesanKosong={lama ? "Tidak ada program lama pada filter ini" : "Belum ada program pada halaman & filter ini"}
                            varian="tabel" kolom={jumlahKolom} data-testid="cb-prog-loading">
                {sortedItems.map((it) => (
                  <tr key={it.id} data-testid={`cb-prog-row-${it.id}`} onClick={() => onBuka(it)}
                      className="hover:bg-slate-50/60 cursor-pointer">
                    <td className="px-3 py-3">
                      <p className="font-mono text-[11px] text-slate-500">{it.code}</p>
                      <p className="text-slate-800">{formatTanggal(aktivasi ? it.created_at : it.tanggal_price_drop)}</p>
                    </td>
                    <td className="px-3 py-3">
                      <p className="font-medium text-slate-900">{it.nama_program}</p>
                      <p className="text-[11px] text-slate-500">{it.supplier_nama}</p>
                      {!halaman || lama ? (
                        <p className="text-[10px] text-indigo-700 mt-0.5" data-testid={`cb-prog-jenis-${it.id}`}>{labelProgram(it)}</p>
                      ) : null}
                    </td>
                    <td className="px-3 py-3 text-xs text-slate-700">{[it.brand, it.cabang].filter(Boolean).join(" · ") || "-"}</td>
                    <td className="px-3 py-3 text-center text-slate-700">{(it.items || []).length}</td>
                    <td className="px-3 py-3 text-xs text-slate-700">{formatTanggal(it.periode_aktivasi_dari)} s.d. {formatTanggal(it.periode_aktivasi_sampai)}</td>
                    <td className="px-3 py-3"><DeadlineBadge deadline={it.deadline_submit} statusBatch={it.status === PROGRAM_STATUS.AKTIF ? "draft" : "lain"} /></td>
                    {denganTotal && <td className="px-3 py-3 text-right">{selTotal(it)}</td>}
                    <td className="px-3 py-3 text-center">
                      <ProgramStatusPill status={it.status} />
                      {it.pengajuan_selesai && (
                        <p className="text-[10px] text-amber-700 mt-0.5" data-testid={`cb-prog-menunggu-pic-${it.id}`}>Menunggu PIC</p>
                      )}
                    </td>
                    <td className="px-3 py-3 text-right">
                      <button onClick={(e) => { e.stopPropagation(); onBuka(it); }}
                              data-testid={`cb-prog-open-${it.id}`} className="text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-0.5 text-xs font-medium">
                        Kelola <ChevronRight className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </LoadingState>
            </tbody>
          </table>
        </TabelDesktop>

        <DaftarKartuMobile testid="cb-prog-kartu-mobile">
          <LoadingState loading={loading} error={loadError} onRetry={() => setMuat((n) => n + 1)} kosong={!loading && !loadError && items.length === 0}
                        pesanKosong={lama ? "Tidak ada program lama pada filter ini" : "Belum ada program pada halaman & filter ini"}
                        varian="kartu" data-testid="cb-prog-loading-m">
            {sortedItems.map((it) => (
              <KartuBaris key={it.id} testid={`cb-prog-card-${it.id}`} onClick={() => onBuka(it)}>
                <KartuBarisHeader
                  kiri={<>
                    <p className="font-mono text-[11px] text-slate-500">{it.code}</p>
                    <p className="text-sm font-semibold text-slate-900">{it.nama_program}</p>
                    <p className="text-[11px] text-slate-500">{it.supplier_nama}</p>
                    {!halaman || lama ? <p className="text-[10px] text-indigo-700">{labelProgram(it)}</p> : null}
                  </>}
                  kanan={<ProgramStatusPill status={it.status} />}
                />
                <KartuBarisMeta label="SKU" value={(it.items || []).length} />
                {aktivasi
                  ? <KartuBarisMeta label="Periode Aktivasi" value={`${formatTanggal(it.periode_aktivasi_dari)} s.d. ${formatTanggal(it.periode_aktivasi_sampai)}`} />
                  : <KartuBarisMeta label="Price Drop" value={formatTanggal(it.tanggal_price_drop)} />}
                {denganTotal && <KartuBarisMeta label={`Total ${istilah}`} value={selTotal(it, "-m")} />}
                <div className="flex items-center justify-between pt-1">
                  <DeadlineBadge deadline={it.deadline_submit} statusBatch={it.status === PROGRAM_STATUS.AKTIF ? "draft" : "lain"} />
                  <span className="text-[11px] text-emerald-700 font-medium inline-flex items-center gap-0.5">
                    Kelola <ChevronRight className="w-3.5 h-3.5" />
                  </span>
                </div>
              </KartuBaris>
            ))}
          </LoadingState>
        </DaftarKartuMobile>
      </div>
      {lamaSeJenis.length > 0 && (
        <details className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 text-sm" data-testid="cb-prog-lama-sejenis">
          <summary className="font-semibold text-slate-800 cursor-pointer">
            {aktivasi ? "Program lama yang ditetapkan Support Aktivasi" : "Program lama (rumus modal, semua kanal)"} · {lamaSeJenis.length} program
          </summary>
          <p className="text-xs text-slate-500 mt-1">Program lama tidak punya kanal, jadi tidak masuk daftar halaman ini. Angka funnel &amp; hangusnya ada di tab Program Lama.</p>
          <ul className="mt-2 divide-y divide-slate-100">
            {lamaSeJenis.map((it) => (
              <li key={it.id}>
                <button onClick={() => onBuka(it)} data-testid={`cb-prog-lama-sejenis-${it.id}`}
                        className="w-full text-left py-2 flex items-center justify-between gap-2 hover:bg-slate-50/60">
                  <span>
                    <span className="font-mono text-[11px] text-slate-500 mr-2">{it.code}</span>
                    <span className="text-slate-900">{it.nama_program}</span>
                    <span className="block text-[10px] text-indigo-700">{labelProgram(it)}</span>
                  </span>
                  <ProgramStatusPill status={it.status} />
                </button>
              </li>
            ))}
          </ul>
        </details>
      )}
    </>
  );
}

// ============ Dashboard ============
// Angka funnel & recovery_rate SEPENUHNYA dihitung backend (`GET /cashback/
// dashboard`) — halaman ini murni menampilkan, tidak menjumlahkan ulang per-
// IMEI (aturan brief: jangan menghitung ulang cashback di klien).
// Halaman jenis × kanal menyaring program lewat parameter backend `jenis`/`kanal`
// (dashboard sebagai TAB halaman itu, brief A9: total Support Aktivasi &
// Cashback muncul di dashboard) — istilah kartu mengikuti jenis halaman.
function CashbackDashboard({ reloadKey, halaman, mode }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const istilah = halaman ? istilahNominal(halaman.jenis) : "Cashback";

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const params = halaman ? { jenis: halaman.jenis, kanal: halaman.kanal } : mode ? { mode } : {};
      const r = await api.get(`${CB_BASE}/dashboard`, { params });
      setData(r.data);
    } catch (e) { setError(e); toast.error(e?.response?.data?.detail || "Gagal memuat dashboard Cashback Claim"); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey]);

  if (loading) return <LoadingState loading varian="kartu" jumlahKartu={6} data-testid="cb-dash-loading" />;
  if (error) return <LoadingState loading={false} error={error} onRetry={load} varian="kartu" data-testid="cb-dash-error" />;

  const f = data.funnel || {};
  const get = (k) => f[k] || { count: 0, total: 0 };
  const recovery = data.recovery_rate;

  const cards = [
    { key: "potential", label: `Potensi ${istilah}`, icon: Percent, tone: "text-slate-900", sub: "seluruh kandidat, termasuk yang belum aktif" },
    { key: "eligible", label: "Eligible (Siap Diklaim)", icon: CheckCircle2, tone: "text-emerald-700", sub: "belum masuk Claim Batch" },
    { key: "pending_activation", label: "Menunggu Aktivasi", icon: Clock, tone: "text-amber-700", sub: "belum ada tanggal aktivasi" },
    { key: "submitted", label: "Disubmit ke Supplier", icon: Send, tone: "text-sky-700", sub: "menunggu keputusan supplier" },
    { key: "approved", label: "Disetujui (Outstanding)", icon: Wallet, tone: "text-amber-700", sub: "menunggu settlement" },
    { key: "rejected", label: "Ditolak Supplier", icon: Ban, tone: "text-rose-700", sub: "bisa diajukan ulang (reclaim)" },
    { key: "received", label: "Diterima (Recovered)", icon: CheckCircle2, tone: "text-emerald-700", sub: "sudah settled/lunas" },
    // Ember PP-UANG (Sep 2026): definisi recovery rate berubah — penolakan
    // supplier sesudah deadline ikut penyebut, dan IMEI di batch draft sesudah
    // deadline dihitung hangus. Kedua kartu ini menampilkan komponen penyebut
    // yang dulu tidak terlihat, supaya angka persen bisa ditelusuri.
    { key: "draft", label: "Masih di Batch Draft", icon: Clock, tone: "text-amber-700", sub: "belum dikirim ke supplier — hangus kalau lewat deadline" },
    { key: "rejected_final", label: "Ditolak Final (Lewat Deadline)", icon: Ban, tone: "text-rose-700", sub: "ditolak & deadline lewat atau program Selesai — ikut penyebut recovery rate" },
  ];

  return (
    <div className="space-y-6" data-testid="cb-dashboard">
      {/* Missed/hangus SENGAJA dipisah dari grid KPI netral — brief butir 25:
          ini inti kerugian modul, tidak boleh terlihat setara angka lain. */}
      <div className="bg-rose-900 text-white rounded-xl shadow-lg p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid="cb-kpi-missed">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-lg bg-white/10 grid place-items-center shrink-0"><AlertTriangle className="w-6 h-6" /></div>
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-rose-200">{istilah} Hangus (Missed Claim)</p>
            <p className="font-display text-2xl md:text-3xl font-bold mt-0.5">{idr(get("missed").total)}</p>
            <p className="text-xs text-rose-200 mt-1">{get("missed").count} unit eligible yang TIDAK PERNAH dikirim ke supplier sebelum deadline (termasuk yang tertinggal di batch draft) — kerugian murni yang sebenarnya bisa dicegah.</p>
          </div>
        </div>
        <div className="text-left sm:text-right shrink-0">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-rose-200">Recovery Rate</p>
          <p className="font-display text-3xl font-bold mt-0.5">{recovery == null ? "-" : `${recovery}%`}</p>
          {/* Rumus SAMA dengan backend `dashboard_cashback` (recovery_rate). */}
          <p className="text-[11px] text-rose-200 mt-1" data-testid="cb-kpi-recovery-rumus">
            diterima ÷ (diterima + hangus + ditolak final)
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => (
          <div key={c.key} data-testid={`cb-kpi-${c.key}`} className="bg-white border border-slate-200 rounded-xl shadow-sm p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="w-9 h-9 rounded-lg bg-slate-50 text-slate-700 grid place-items-center"><c.icon className="w-4.5 h-4.5" /></div>
              <span className="text-[10px] text-slate-400">{get(c.key).count} unit</span>
            </div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{c.label}</p>
            <p className={`font-display text-xl font-bold mt-1 ${c.tone}`}>{idr(get(c.key).total)}</p>
            <p className="text-[11px] text-slate-500 mt-1">{c.sub}</p>
          </div>
        ))}
      </div>

      <DashboardProgramSelesai data={data.program_selesai} />

      <p className="text-xs text-slate-500">Per {data.as_of ? formatTanggal(data.as_of) : "-"} · {data.jumlah_program_aktif || 0} program aktif</p>
    </div>
  );
}

// ============ Laporan Periode (brief owner 19 Agu #10) ============
// Filter by rentang tanggal -> SKU apa saja yang TERCOVER Price Protection
// pada periode itu (periode_aktivasi program beririsan dengan rentang yang
// dipilih), lengkap dengan detail per unit: masuk tanggal berapa, terjual
// kemana, dan modal AKTUAL — SEPENUHNYA dari backend (`GET /cashback/laporan/
// periode`), tidak dihitung ulang di klien (aturan brief: jangan menghitung
// ulang cashback/modal di frontend).
// Halaman jenis × kanal: laporan menyaring program lewat `jenis`/`kanal` yang
// sama dengan backend (modal tetap tampil — keputusan owner 19 Agu #10).
function LaporanPeriode({ halaman }) {
  const bulanIni = hariIniISO().slice(0, 7);
  const [dari, setDari] = useState(`${bulanIni}-01`);
  const [sampai, setSampai] = useState(hariIniISO());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [skuFilter, setSkuFilter] = useState("");
  const [expandedImei, setExpandedImei] = useState(() => new Set());

  const load = async () => {
    if (!dari || !sampai) return;
    if (sampai < dari) { toast.error("Tanggal sampai tidak boleh sebelum tanggal mulai"); return; }
    setLoading(true); setError(null);
    try {
      const saring = halaman ? { jenis: halaman.jenis, kanal: halaman.kanal } : {};
      const r = await api.get(`${CB_BASE}/laporan/periode`, { params: { dari, sampai, ...saring } });
      setData(r.data);
    } catch (e) { setError(e); toast.error(e?.response?.data?.detail || "Gagal memuat laporan periode"); }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const filteredItems = useMemo(() => {
    const all = data?.items || [];
    if (!skuFilter) return all;
    return all.filter((it) => it.sku === skuFilter);
  }, [data, skuFilter]);

  // Kedua tabel di laporan ini adalah hasil AGREGASI/rincian backend untuk
  // rentang tanggal yang dipilih (bukan potongan N-teratas dari query
  // mentah) — aman disortir tanpa BatasDaftarNotice. Baris trade-in
  // (`penjualan` >1x) tetap menempel ke IMEI induknya karena disortir sebagai
  // satu grup lewat array `items` di luar (`sortRows` hanya menata ULANG
  // urutan grup, tidak memecahnya).
  const skuSort = useSortState(null);
  const sortedSkus = sortRows(data?.skus || [], skuSort.sortKey, skuSort.sortDir, {
    sku: (s) => s.sku || "",
    produk: (s) => s.nama_produk || "",
    unit: (s) => s.jumlah_unit || 0,
    modal: (s) => s.total_modal || 0,
    cashback: (s) => s.total_cashback || 0,
  });

  const itemSort = useSortState(null);
  const items = sortRows(filteredItems, itemSort.sortKey, itemSort.sortDir, {
    imei: (it) => it.imei || "",
    sku: (it) => it.sku || "",
    masuk: (it) => it.tanggal_masuk || "",
    modal: (it) => it.harga_perolehan || 0,
    status: (it) => it.status || "",
  });

  const toggleExpand = (imei) => setExpandedImei((prev) => { const n = new Set(prev); n.has(imei) ? n.delete(imei) : n.add(imei); return n; });

  return (
    <div className="space-y-4" data-testid="cb-laporan-periode">
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 space-y-3">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500 font-medium">Dari</span>
            <DatePicker value={dari} onChange={setDari} data-testid="cb-laporan-dari" />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500 font-medium">Sampai</span>
            <DatePicker value={sampai} onChange={setSampai} data-testid="cb-laporan-sampai" />
          </div>
          <button onClick={load} disabled={loading} data-testid="cb-laporan-terapkan"
                  className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50 inline-flex items-center gap-1.5">
            <CalendarRange className="w-4 h-4" /> {loading ? "Memuat..." : "Terapkan"}
          </button>
          <p className="text-xs text-slate-500">Periode aktivasi program yang beririsan dengan rentang ini.</p>
        </div>
      </div>

      {error && <LoadingState loading={false} error={error} onRetry={load} varian="kartu" data-testid="cb-laporan-error" />}

      {!error && data && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Program Tercover</p>
              <p className="font-display text-2xl font-bold text-slate-900 mt-1">{data.jumlah_program}</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">SKU Tercover</p>
              <p className="font-display text-2xl font-bold text-slate-900 mt-1">{data.skus.length}</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Total Unit</p>
              <p className="font-display text-2xl font-bold text-slate-900 mt-1">{data.count}</p>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
              <PackageSearch className="w-4 h-4 text-slate-500" />
              <p className="text-sm font-semibold text-slate-800">SKU yang Tercover</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[11px] uppercase text-slate-500">
                  <tr>
                    <SortTh label="SKU" sortKeyName="sku" sortKey={skuSort.sortKey} sortDir={skuSort.sortDir} onSort={skuSort.toggle} testid="cb-laporan-sku-sort-sku" />
                    <SortTh label="Produk" sortKeyName="produk" sortKey={skuSort.sortKey} sortDir={skuSort.sortDir} onSort={skuSort.toggle} testid="cb-laporan-sku-sort-produk" />
                    <th className="text-left px-3 py-2">Program</th>
                    <SortTh label="Jumlah Unit" sortKeyName="unit" sortKey={skuSort.sortKey} sortDir={skuSort.sortDir} onSort={skuSort.toggle} align="right" testid="cb-laporan-sku-sort-unit" />
                    <SortTh label="Total Modal" sortKeyName="modal" sortKey={skuSort.sortKey} sortDir={skuSort.sortDir} onSort={skuSort.toggle} align="right" testid="cb-laporan-sku-sort-modal" />
                    <SortTh label="Total Cashback" sortKeyName="cashback" sortKey={skuSort.sortKey} sortDir={skuSort.sortDir} onSort={skuSort.toggle} align="right" testid="cb-laporan-sku-sort-cashback" />
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {data.skus.length === 0 && (
                    <tr><td colSpan={7} className="text-center text-slate-400 py-6 text-sm">Tidak ada SKU tercover pada periode ini</td></tr>
                  )}
                  {sortedSkus.map((s) => (
                    <tr key={s.sku} data-testid={`cb-laporan-sku-${s.sku}`} className={skuFilter === s.sku ? "bg-emerald-50/50" : ""}>
                      <td className="px-3 py-2 font-mono text-xs">{s.sku}</td>
                      <td className="px-3 py-2">{s.nama_produk}</td>
                      <td className="px-3 py-2 text-xs text-slate-500">{s.program_codes.join(", ")}</td>
                      <td className="px-3 py-2 text-right">{s.jumlah_unit}</td>
                      <td className="px-3 py-2 text-right font-mono">{idr(s.total_modal)}</td>
                      <td className="px-3 py-2 text-right font-mono text-emerald-700">{idr(s.total_cashback)}</td>
                      <td className="px-3 py-2 text-right">
                        <button onClick={() => setSkuFilter((cur) => cur === s.sku ? "" : s.sku)} data-testid={`cb-laporan-sku-filter-${s.sku}`}
                                className="text-[11px] text-emerald-700 hover:text-emerald-900 font-medium">
                          {skuFilter === s.sku ? "Lihat semua" : "Lihat detail"}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100">
              <p className="text-sm font-semibold text-slate-800">Detail Unit{skuFilter ? ` — ${skuFilter}` : ""}</p>
              <p className="text-[11px] text-slate-500 mt-0.5">Masuk tanggal berapa, terjual kemana, dan modal aktual per IMEI.</p>
            </div>
            <div className="overflow-x-auto max-h-[480px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[11px] uppercase text-slate-500 sticky top-0">
                  <tr>
                    <SortTh label="IMEI" sortKeyName="imei" sortKey={itemSort.sortKey} sortDir={itemSort.sortDir} onSort={itemSort.toggle} testid="cb-laporan-detail-sort-imei" />
                    <SortTh label="SKU / Produk" sortKeyName="sku" sortKey={itemSort.sortKey} sortDir={itemSort.sortDir} onSort={itemSort.toggle} testid="cb-laporan-detail-sort-sku" />
                    <SortTh label="Masuk Tanggal" sortKeyName="masuk" sortKey={itemSort.sortKey} sortDir={itemSort.sortDir} onSort={itemSort.toggle} testid="cb-laporan-detail-sort-masuk" />
                    <SortTh label="Modal Aktual" sortKeyName="modal" sortKey={itemSort.sortKey} sortDir={itemSort.sortDir} onSort={itemSort.toggle} align="right" testid="cb-laporan-detail-sort-modal" />
                    <th className="text-center px-2 py-2">Status</th>
                    <th className="text-left px-2 py-2">Terjual Ke</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  <LoadingState loading={loading} error={null} kosong={!loading && items.length === 0}
                                pesanKosong="Tidak ada unit pada filter ini" varian="tabel" kolom={6} data-testid="cb-laporan-detail-loading">
                    {items.map((it) => {
                      const expanded = expandedImei.has(it.imei);
                      const penjualan = it.penjualan || [];
                      return (
                        <Fragment key={it.imei}>
                          <tr data-testid={`cb-laporan-row-${it.imei}`}>
                            <td className="px-2 py-2 font-mono text-xs">{it.imei}</td>
                            <td className="px-2 py-2">
                              <p className="text-slate-900">{it.nama_produk}</p>
                              <p className="text-[10px] text-slate-500 font-mono">{it.sku}</p>
                            </td>
                            <td className="px-2 py-2 text-xs">{formatTanggal(it.tanggal_masuk)}</td>
                            <td className="px-2 py-2 text-right font-mono">{idr(it.harga_perolehan)}</td>
                            <td className="px-2 py-2 text-center"><EligibilityPill status={it.status} /></td>
                            <td className="px-2 py-2 text-xs">
                              {!it.sudah_terjual ? (
                                <span className="text-slate-400">Belum terjual</span>
                              ) : penjualan.length === 1 ? (
                                <span>{penjualan[0].nama_pelanggan || "-"} <span className="text-slate-400">· {formatTanggal(penjualan[0].tanggal)}{penjualan[0].branch ? ` · ${penjualan[0].branch}` : ""}</span></span>
                              ) : (
                                <button onClick={() => toggleExpand(it.imei)} data-testid={`cb-laporan-expand-${it.imei}`}
                                        className="text-emerald-700 hover:text-emerald-900 font-medium">
                                  {penjualan.length}x terjual (riwayat tukar tambah) — {expanded ? "tutup" : "lihat"}
                                </button>
                              )}
                            </td>
                          </tr>
                          {expanded && penjualan.length > 1 && penjualan.map((p, i) => (
                            <tr key={`${it.imei}-p-${i}`} className="bg-slate-50/60">
                              <td></td>
                              <td colSpan={5} className="px-2 py-1.5 text-xs text-slate-600">
                                {formatTanggal(p.tanggal)} — {p.nama_pelanggan || "-"}{p.branch ? ` · ${p.branch}` : ""}{p.nama_penjual ? ` · penjual: ${p.nama_penjual}` : ""}
                              </td>
                            </tr>
                          ))}
                        </Fragment>
                      );
                    })}
                  </LoadingState>
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ============ Form: Program Baru / Edit ============
// Varian per jenis (brief 22 Sep hal. 3 & 7): Support Aktivasi tanpa Tanggal
// Price Drop & Harga Baru, "Harga Lama" jadi "Nominal Support Aktivasi", Periode
// Klaim diisi otomatis dari kejadian; Support Cashback = form lama. Jenis &
// kanal TERKUNCI dari halaman (dan tidak bisa diubah sesudah dibuat). Program
// LAMA yang diedit memakai form lama apa adanya (tanpa jenis/kanal).
// Body dibangun `bangunBodyProgram` (fungsi murni, diuji node + ASGI).
function FormProgram({ program, jenisBaru = null, kanalBaru = null, cabangOptions, onClose, onCreated }) {
  const isEdit = !!program;
  const berjenisEdit = isEdit && modeProgram(program) === MODE.KEANGGOTAAN;
  const [jenisPilih, setJenisPilih] = useState(jenisBaru || "");
  const [kanalPilih, setKanalPilih] = useState(kanalBaru || "");
  // Edit program lama → null (form lama); program baru dari halaman "Semua
  // Program" → wajib memilih jenis & kanal dulu (program baru selalu berjenis).
  const jenis = isEdit ? (berjenisEdit ? program.jenis_efektif : null) : (jenisPilih || null);
  const kanal = isEdit ? (berjenisEdit ? program.kanal_efektif : null) : (kanalPilih || null);
  const pilihTerkunci = isEdit || !!(jenisBaru && kanalBaru);
  const aktivasi = jenis === JENIS.AKTIVASI;

  const [f, setF] = useState(() => program ? {
    nama_program: program.nama_program, supplier_nama: program.supplier_nama,
    brand: program.brand || "", cabang: program.cabang || "",
    items: (program.items || []).map((it) => (berjenisEdit && program.jenis_efektif === JENIS.AKTIVASI
      ? { sku: it.sku, nominal_support: it.nominal_support }
      : { sku: it.sku, harga_lama: it.harga_lama, harga_baru: it.harga_baru })),
    tanggal_price_drop: program.tanggal_price_drop || "", periode_aktivasi_dari: program.periode_aktivasi_dari,
    periode_aktivasi_sampai: program.periode_aktivasi_sampai,
    periode_klaim_dari: program.periode_klaim_dari || "", periode_klaim_sampai: program.periode_klaim_sampai || "",
    deadline_submit: program.deadline_submit, metode_settlement_default: program.metode_settlement_default || "",
    keterangan: program.keterangan || "",
  } : { ...EMPTY_FORM, items: [itemKosong(jenisBaru)] });
  const [busy, setBusy] = useState(false);
  const [katalog, setKatalog] = useState([]);
  const [supplierOpts, setSupplierOpts] = useState([]);
  const [brandOpts, setBrandOpts] = useState([]);

  // Ganti jenis (hanya di halaman Semua Program, sebelum dibuat): isi baris SKU
  // mengikuti jenis — nominal support dan harga lama/baru bukan medan yang sama.
  const gantiJenis = (j) => {
    setJenisPilih(j);
    setF((prev) => ({ ...prev, items: prev.items.map((it) => ({ ...itemKosong(j || null), sku: it.sku })) }));
  };

  // Katalog SKU untuk autocomplete — endpoint ini HANYA butuh login (tidak
  // digerbangi izin `inventory_produk`), sengaja dipilih ketimbang `/inventory/
  // products/lookup` supaya custom role yang cuma dikasih `finance_cashback_claim`
  // (tanpa izin Inventory) tetap bisa memilih SKU tanpa 403.
  useEffect(() => {
    api.get("/finance/persediaan/products").then((r) => setKatalog(r.data.items || [])).catch(() => {});
  }, []);

  // Supplier & Brand (brief owner 19 Agu #8): dropdown TERBATAS ke master —
  // "hanya bisa memilih supplier dan brand terdaftar". Endpoint RINGKAS milik
  // modul ini sendiri (`/cashback/master/...`, gerbang `finance_cashback_claim.
  // view`), BUKAN `/supplier` atau `/inventory/master-opsi` langsung — kedua
  // itu digerbangi izin Inventory yang tidak tentu dipegang pengguna Finance
  // (lihat docstring backend `daftar_supplier_untuk_form`/`daftar_brand_untuk_form`).
  useEffect(() => {
    api.get(`${CB_BASE}/master/supplier`).then((r) => setSupplierOpts(r.data.items || [])).catch(() => {});
    api.get(`${CB_BASE}/master/brand`).then((r) => setBrandOpts(r.data.items || [])).catch(() => {});
  }, []);

  // Kompatibilitas mundur: program LAMA bisa punya `supplier_nama`/`brand`
  // berupa teks yang TIDAK persis cocok dengan master (beda ejaan/besar-kecil,
  // atau supplier/brand yang sudah dihapus/diganti nama). Kalau dropdown HANYA
  // berisi opsi master, membuka form edit akan diam-diam menjatuhkan pilihan ke
  // opsi lain (mengubah data tanpa disadari staf) atau membuat dropdown
  // tampak kosong. Nilai lama yang tidak ketemu di master disisipkan sebagai
  // opsi tambahan berlabel jelas, supaya form terbuka dengan nilai yang BENAR
  // dan hanya berubah kalau staf sendiri memilih opsi lain.
  const supplierNames = useMemo(() => supplierOpts.map((s) => s.nama), [supplierOpts]);
  // Cocokkan EXACT dulu (`<select>` HTML case-sensitive terhadap `value` —
  // kalau nilai lama cuma beda huruf besar-kecil dari opsi master, native
  // select tidak akan menandai opsi manapun terpilih walau datanya "cocok").
  // Peringatan "(nilai lama, tidak di master)" HANYA muncul kalau memang
  // tidak ketemu sama sekali (case-insensitive) — beda besar-kecil semata
  // BUKAN masalah nyata (backend menormalisasi ke ejaan kanonik begitu staf
  // menyentuhnya), jadi tidak perlu menakut-nakuti dengan label itu.
  const supplierSelectOpts = useMemo(() => {
    if (isEdit && program.supplier_nama && !supplierNames.includes(program.supplier_nama)) {
      const benarBenarTakDikenal = !supplierNames.some((n) => n.toLowerCase() === program.supplier_nama.toLowerCase());
      const label = benarBenarTakDikenal ? `${program.supplier_nama} (nilai lama, tidak di master)` : program.supplier_nama;
      return [{ value: program.supplier_nama, label }, ...supplierNames.map((n) => ({ value: n, label: n }))];
    }
    return supplierNames.map((n) => ({ value: n, label: n }));
  }, [supplierNames, isEdit, program]);
  const brandSelectOpts = useMemo(() => {
    if (isEdit && program.brand && !brandOpts.includes(program.brand)) {
      const benarBenarTakDikenal = !brandOpts.some((n) => n.toLowerCase() === program.brand.toLowerCase());
      const label = benarBenarTakDikenal ? `${program.brand} (nilai lama, tidak di master)` : program.brand;
      return [{ value: program.brand, label }, ...brandOpts.map((n) => ({ value: n, label: n }))];
    }
    return brandOpts.map((n) => ({ value: n, label: n }));
  }, [brandOpts, isEdit, program]);

  const setItem = (idx, patch) => setF((prev) => ({ ...prev, items: prev.items.map((it, i) => i === idx ? { ...it, ...patch } : it) }));
  const addItem = () => setF((prev) => ({ ...prev, items: [...prev.items, itemKosong(jenis)] }));
  const delItem = (idx) => setF((prev) => ({ ...prev, items: prev.items.filter((_, i) => i !== idx) }));

  const valid = formProgramValid(f, jenis) && (isEdit || !!(jenis && kanal));

  const submit = async () => {
    if (!valid) return;
    setBusy(true);
    try {
      const body = bangunBodyProgram(f, jenis, kanal);
      const r = isEdit ? await api.put(`${CB_BASE}/programs/${program.id}`, body) : await api.post(`${CB_BASE}/programs`, body);
      toast.success(r.data.message || "Program tersimpan ✓");
      onCreated?.(r.data.item);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan Program Price Protection"); }
    finally { setBusy(false); }
  };

  const judul = isEdit ? `Edit Program ${program.code}`
    : jenis ? `Program ${LABEL_JENIS[jenis]}${kanal ? ` ${LABEL_KANAL[kanal]}` : ""} Baru` : "Program Price Protection Baru";

  return (
    <Modal title={judul} icon={Percent}
           onClose={onClose} onSubmit={submit} submitLabel={isEdit ? "Simpan Perubahan" : "Buat Program"}
           busy={busy} disabled={!valid} testid="cb-prog-form-modal" wide
           // BERBAHAYA: daftar SKU + harga lama/baru dinamis, bisa berbaris-baris —
           // menutup tanpa sengaja berarti mengetik ulang semuanya (brief owner 21 Agu).
           dangerous>
      {pilihTerkunci ? (
        (jenis || !isEdit) && (
          <div className="flex items-center gap-2 flex-wrap text-xs" data-testid="cb-prog-form-jenis-kanal">
            <span className="px-2 py-1 rounded bg-indigo-50 text-indigo-800 border border-indigo-200" data-testid="cb-prog-form-jenis">{LABEL_JENIS[jenis]}</span>
            <span className="px-2 py-1 rounded bg-indigo-50 text-indigo-800 border border-indigo-200" data-testid="cb-prog-form-kanal">{LABEL_KANAL[kanal]}</span>
            <span className="text-slate-500">Jenis & kanal tidak bisa diubah sesudah program dibuat.</span>
          </div>
        )
      ) : (
        <div className="grid grid-cols-2 gap-3">
          <Field label="Jenis Program">
            <select value={jenisPilih} onChange={(e) => gantiJenis(e.target.value)} data-testid="cb-prog-form-jenis" className={inputCls}>
              <option value="">Pilih jenis...</option>
              {Object.values(JENIS).map((j) => <option key={j} value={j}>{LABEL_JENIS[j]}</option>)}
            </select>
          </Field>
          <Field label="Kanal">
            <select value={kanalPilih} onChange={(e) => setKanalPilih(e.target.value)} data-testid="cb-prog-form-kanal" className={inputCls}>
              <option value="">Pilih kanal...</option>
              {Object.entries(LABEL_KANAL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </Field>
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        <Field label="Nama Program" col2>
          <input value={f.nama_program} onChange={(e) => setF({ ...f, nama_program: e.target.value })} data-testid="cb-prog-form-nama" className={inputCls}
                 placeholder={aktivasi ? "mis. Support Aktivasi TAM Oktober 2026" : "mis. Price Protection Agustus 2026"} />
        </Field>
        <Field label="Nama Supplier" hint={supplierSelectOpts.length === 0 ? "Belum ada supplier terdaftar — daftarkan dulu lewat Master Supplier" : undefined}>
          <select value={f.supplier_nama} onChange={(e) => setF({ ...f, supplier_nama: e.target.value })} data-testid="cb-prog-form-supplier" className={inputCls}>
            <option value="">Pilih supplier...</option>
            {supplierSelectOpts.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </Field>
        <Field label={aktivasi ? "Produk (Brand, opsional)" : "Brand Produk (opsional)"}>
          <select value={f.brand} onChange={(e) => setF({ ...f, brand: e.target.value })} data-testid="cb-prog-form-brand" className={inputCls}>
            <option value="">- Tidak spesifik -</option>
            {brandSelectOpts.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </Field>
        <Field label="Cabang (opsional)" hint={aktivasi ? "Informasi saja — unit tidak disaring per cabang." : undefined}>
          <select value={f.cabang} onChange={(e) => setF({ ...f, cabang: e.target.value })} data-testid="cb-prog-form-cabang" className={inputCls}>
            {cabangOptions.map((c) => <option key={c || "_none"} value={c}>{c || "-"}</option>)}
          </select>
        </Field>
        {!aktivasi && (
          <Field label="Tanggal Price Drop">
            <DatePicker value={f.tanggal_price_drop} onChange={(v) => setF({ ...f, tanggal_price_drop: v })} data-testid="cb-prog-form-tgl-drop" />
          </Field>
        )}
        <Field label={aktivasi ? "Deadline Aktivasi" : "Deadline Submit Klaim"}
               hint={aktivasi ? "Batas kirim data aktivasi ke supplier — lewat tanggal ini, support hangus." : "Batas kirim ke supplier — lewat tanggal ini, cashback hangus."}>
          <DatePicker value={f.deadline_submit} onChange={(v) => setF({ ...f, deadline_submit: v })} data-testid="cb-prog-form-deadline" />
        </Field>
        <Field label="Periode Aktivasi Dari" hint={aktivasi ? "Wajib diisi" : "Kosongkan = sama dengan tanggal price drop"}>
          <DatePicker value={f.periode_aktivasi_dari} onChange={(v) => setF({ ...f, periode_aktivasi_dari: v })} data-testid="cb-prog-form-periode-dari" />
        </Field>
        <Field label="Periode Aktivasi Sampai">
          <DatePicker value={f.periode_aktivasi_sampai} onChange={(v) => setF({ ...f, periode_aktivasi_sampai: v })} data-testid="cb-prog-form-periode-sampai" />
        </Field>
        {aktivasi ? (
          <div className="col-span-2 bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-600" data-testid="cb-prog-form-klaim-otomatis">
            <b>Periode Klaim</b> diisi otomatis dari kejadian: <b>Dari</b> = tanggal data pertama dikirim ke supplier,
            <b> Sampai</b> = tanggal CN/uang terakhir cair penuh. Tampil di Info Program.
          </div>
        ) : (
          <>
            <Field label="Periode Klaim Dari (opsional)">
              <DatePicker value={f.periode_klaim_dari} onChange={(v) => setF({ ...f, periode_klaim_dari: v })} data-testid="cb-prog-form-klaim-dari" />
            </Field>
            <Field label="Periode Klaim Sampai (opsional)">
              <DatePicker value={f.periode_klaim_sampai} onChange={(v) => setF({ ...f, periode_klaim_sampai: v })} data-testid="cb-prog-form-klaim-sampai" />
            </Field>
          </>
        )}
        <Field label="Metode Settlement Default (opsional)" col2>
          <select value={f.metode_settlement_default} onChange={(e) => setF({ ...f, metode_settlement_default: e.target.value })} data-testid="cb-prog-form-metode" className={inputCls}>
            <option value="">Belum ditentukan</option>
            {Object.entries(METODE_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </Field>
        <Field label="Keterangan (opsional)" col2>
          <textarea rows={2} value={f.keterangan} onChange={(e) => setF({ ...f, keterangan: e.target.value })} data-testid="cb-prog-form-ket" className={inputCls + " h-auto py-2"} />
        </Field>
      </div>

      <div className="pt-2 border-t border-slate-100">
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-semibold text-slate-800">{aktivasi ? "Produk / SKU & Nominal Support Aktivasi" : "Produk / SKU yang Price Drop"}</p>
          <button type="button" onClick={addItem} data-testid="cb-prog-form-item-add"
                  className="text-xs font-medium text-emerald-700 hover:text-emerald-800 inline-flex items-center gap-1">
            <PlusCircle className="w-3.5 h-3.5" /> Tambah SKU
          </button>
        </div>
        <datalist id="cb-sku-datalist">
          {katalog.map((p) => <option key={p.sku} value={p.sku}>{p.nama} {p.brand ? `(${p.brand})` : ""}</option>)}
        </datalist>
        <div className="space-y-2">
          {f.items.map((it, idx) => {
            const hargaTidakValid = !aktivasi && it.harga_lama && it.harga_baru && Number(it.harga_baru) >= Number(it.harga_lama);
            return (
              <div key={idx} className="grid grid-cols-12 gap-2 items-start">
                <div className={aktivasi ? "col-span-7" : "col-span-5"}>
                  <input list="cb-sku-datalist" value={it.sku} onChange={(e) => setItem(idx, { sku: e.target.value })}
                         placeholder="SKU" data-testid={`cb-prog-form-item-sku-${idx}`} className={inputCls} />
                </div>
                {aktivasi ? (
                  <div className="col-span-4">
                    <InputRupiah value={it.nominal_support} onChange={(n) => setItem(idx, { nominal_support: n })} placeholder="Nominal Support Aktivasi"
                                 testid={`cb-prog-form-item-nominal-${idx}`} className={inputCls} />
                  </div>
                ) : (
                  <>
                    <div className="col-span-3">
                      <InputRupiah value={it.harga_lama} onChange={(n) => setItem(idx, { harga_lama: n })} placeholder="Harga lama"
                                   testid={`cb-prog-form-item-lama-${idx}`} className={inputCls} />
                    </div>
                    <div className="col-span-3">
                      <InputRupiah value={it.harga_baru} onChange={(n) => setItem(idx, { harga_baru: n })} placeholder="Harga baru"
                                   testid={`cb-prog-form-item-baru-${idx}`} className={inputCls} />
                    </div>
                  </>
                )}
                <div className="col-span-1 flex items-center h-10">
                  {f.items.length > 1 && (
                    <button type="button" onClick={() => delItem(idx)} data-testid={`cb-prog-form-item-del-${idx}`}
                            className="text-rose-500 hover:text-rose-700"><Trash2 className="w-4 h-4" /></button>
                  )}
                </div>
                {hargaTidakValid && (
                  <p className="col-span-12 text-[11px] text-rose-600 -mt-1">Harga baru harus lebih rendah dari harga lama (kalau tidak ada penurunan, tidak ada cashback).</p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </Modal>
  );
}

// ============ Detail Program: kandidat, impor aktivasi, lampiran ============
function ProgramDetailModal({ program, onClose, onChanged, onDeleted, onEdit, navigate }) {
  const { can } = useAuth();
  const [tab, setTab] = useState("kandidat");
  const [showAtt, setShowAtt] = useState(false);
  const [busy, setBusy] = useState(false);

  const canUpdate = can("finance_cashback_claim", "update");
  const canDelete = can("finance_cashback_claim", "delete");
  // Program Selesai / menunggu keputusan PIC (PP4): edit, tutup, hapus, impor,
  // pindah, dan klaim baru ditolak backend 409 — keputusannya dibaca dari
  // `terkunci_alasan` yang dikirim backend, bukan dinilai ulang di sini.
  const terkunci = !!program.terkunci_alasan;
  // Program jenis baru tidak bisa "Tutup Program" (backend 400) — penutupnya
  // lewat Ajukan Selesai.
  const bisaTutup = program.status === PROGRAM_STATUS.AKTIF && program.mode_mesin !== MODE_KEANGGOTAAN;
  // Program LAMA yang jenisnya belum ditetapkan (PP2): alat "Jadikan Program
  // Support Aktivasi" / "Tetapkan: Support Cashback". Keputusan jenis & mode
  // dari backend (`jenis_efektif`/`mode_efektif`); mode mana yang boleh dipakai
  // dinilai backend di pratinjau modalnya.
  const belumDitetapkan = modeProgram(program) === MODE.LAMA && !program.jenis_efektif;
  const aktivasi = program.jenis_efektif === JENIS.AKTIVASI;
  const berjenis = modeProgram(program) === MODE.KEANGGOTAAN;
  const [tetapkan, setTetapkan] = useState(null);
  // Periode Klaim program Support Aktivasi diturunkan dari kejadian (detail
  // program), tidak ikut di dokumen daftar.
  const [periodeKlaim, setPeriodeKlaim] = useState(program.periode_klaim_kejadian || null);
  useEffect(() => {
    let batal = false;
    api.get(`${CB_BASE}/programs/${program.id}`)
      .then((r) => { if (!batal) setPeriodeKlaim(r.data.item?.periode_klaim_kejadian || null); })
      .catch(() => {});
    return () => { batal = true; };
  }, [program.id]);
  const teksPeriodeKlaim = periodeKlaim && periodeKlaim.dari
    ? `${formatTanggal(periodeKlaim.dari)} s.d. ${periodeKlaim.sampai ? formatTanggal(periodeKlaim.sampai) : "belum cair"}`
    : "-";

  // Muat ulang dokumen program sesudah aksi Selesai supaya status & kunci ikut segar.
  const muatUlangProgram = async () => {
    try {
      const r = await api.get(`${CB_BASE}/programs/${program.id}`);
      onChanged(r.data.item);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal memuat ulang program"); }
  };

  const doTutup = async () => {
    if (!window.confirm(`Tutup program ${program.code}? Program yang ditutup berhenti dideteksi kandidat baru — klaim yang sudah ada tetap bisa diproses.`)) return;
    setBusy(true);
    try {
      const r = await api.post(`${CB_BASE}/programs/${program.id}/tutup`);
      toast.success(r.data.message);
      onChanged({ ...program, status: PROGRAM_STATUS.DITUTUP });
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menutup program"); }
    finally { setBusy(false); }
  };

  const doHapus = async () => {
    if (!window.confirm(`Hapus program ${program.code}? Aksi ini tidak bisa dibatalkan.`)) return;
    setBusy(true);
    try {
      const r = await api.delete(`${CB_BASE}/programs/${program.id}`);
      toast.success(r.data.message);
      onDeleted();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menghapus program"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} data-testid="cb-prog-detail-modal"
           className="bg-white rounded-2xl w-full max-w-6xl max-h-[92vh] flex flex-col shadow-2xl">
        <div className="p-5 border-b border-slate-100 flex items-start justify-between gap-3">
          <div>
            <p className="font-mono text-xs text-slate-500">{program.code}</p>
            <h3 className="font-display font-bold text-lg text-slate-900 mt-0.5">{program.nama_program}</h3>
            <p className="text-sm text-slate-600">{program.supplier_nama}{program.brand ? ` · ${program.brand}` : ""}{program.cabang ? ` · ${program.cabang}` : ""}</p>
            <p className="text-[11px] text-indigo-700 font-medium mt-0.5" data-testid="cb-prog-detail-jenis">{labelProgram(program)}</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <ProgramStatusPill status={program.status} />
            <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
          </div>
        </div>
        {terkunci && (
          <div className="mx-5 mt-3 bg-indigo-50 border border-indigo-200 rounded-lg p-2.5 text-xs text-indigo-900 flex items-start gap-2" data-testid="cb-prog-terkunci-info">
            <Lock className="w-4 h-4 mt-0.5 shrink-0" /> <span>{program.terkunci_alasan}</span>
          </div>
        )}

        <div className="border-b border-slate-100 flex items-center gap-1 px-2 overflow-x-auto">
          {[
            { key: "kandidat", label: berjenis ? "Unit & Eligibility" : "Kandidat & Eligibility" },
            { key: "info", label: "Info Program" },
            { key: "lampiran", label: "Lampiran" },
          ].map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`cb-prog-tab-${t.key}`}
                    className={`px-4 h-11 text-sm font-medium border-b-2 whitespace-nowrap transition ${tab === t.key ? "border-emerald-600 text-emerald-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
              {t.label}
            </button>
          ))}
        </div>

        <div className="p-5 overflow-y-auto flex-1">
          {tab === "kandidat" && (
            <KandidatManager program={program} navigate={navigate} onClaimCreated={onClose} />
          )}

          {tab === "info" && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {aktivasi ? (
                  <>
                    <InfoRow label="Deadline Aktivasi" value={formatTanggal(program.deadline_submit)} />
                    <InfoRow label="Periode Aktivasi" value={`${formatTanggal(program.periode_aktivasi_dari)} s.d. ${formatTanggal(program.periode_aktivasi_sampai)}`} />
                    <div data-testid="cb-prog-periode-klaim-kejadian">
                      <InfoRow label="Periode Klaim (otomatis: data dikirim ke supplier s.d. CN cair)" value={teksPeriodeKlaim} />
                      {periodeKlaim && periodeKlaim.keterangan && <p className="text-[11px] text-slate-500 mt-0.5">{periodeKlaim.keterangan}</p>}
                    </div>
                  </>
                ) : (
                  <>
                    <InfoRow label="Tanggal Price Drop" value={formatTanggal(program.tanggal_price_drop)} />
                    <InfoRow label="Deadline Submit" value={formatTanggal(program.deadline_submit)} />
                    <InfoRow label="Periode Aktivasi" value={`${formatTanggal(program.periode_aktivasi_dari)} s.d. ${formatTanggal(program.periode_aktivasi_sampai)}`} />
                    <InfoRow label="Periode Klaim" value={program.periode_klaim_dari ? `${formatTanggal(program.periode_klaim_dari)} s.d. ${formatTanggal(program.periode_klaim_sampai)}` : "-"} />
                  </>
                )}
                <InfoRow label="Jenis · Kanal" value={labelProgram(program)} />
                <InfoRow label="Metode Settlement Default" value={METODE_LABEL[program.metode_settlement_default] || "-"} />
                <InfoRow label="Dibuat oleh" value={`${program.created_by_name || program.created_by || "-"} · ${formatTanggal(program.created_at, { denganJam: true })}`} />
                {program.konversi && (
                  <InfoRow label="Penetapan Jenis" col2
                           value={`${LABEL_JENIS[program.konversi.jenis] || "-"} (${program.konversi.mode}) oleh ${program.konversi.oleh_nama || program.konversi.oleh} · ${formatTanggal(program.konversi.at, { denganJam: true })} — ${program.konversi.alasan}`} />
                )}
                {program.keterangan && <InfoRow label="Keterangan" value={program.keterangan} col2 />}
              </div>
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">{aktivasi ? "Daftar SKU & Nominal Support Aktivasi" : "Daftar SKU Price Drop"}</p>
                <TabelSkuProgram program={program} />
              </div>
              <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-slate-100">
                {canUpdate && !terkunci && <ActionBtn icon={Pencil} label="Edit Program" onClick={onEdit} testid="cb-prog-act-edit" />}
                {canUpdate && bisaTutup && !terkunci && <ActionBtn icon={Lock} label="Tutup Program" onClick={doTutup} testid="cb-prog-act-tutup" disabled={busy} />}
                {canDelete && !terkunci && <ActionBtn icon={Trash2} label="Hapus Program" onClick={doHapus} testid="cb-prog-act-hapus" tone="rose" disabled={busy} />}
              </div>
              {canUpdate && !terkunci && belumDitetapkan && (
                <div className="pt-3 border-t border-slate-100 space-y-2" data-testid="cb-prog-tetapkan-bagian">
                  <p className="text-xs text-slate-600">Program lama ini belum punya jenis — klaimnya mengunci Support Aktivasi dan Support Cashback sekaligus.</p>
                  <div className="flex flex-wrap items-center gap-2">
                    <ActionBtn icon={Tags} label="Jadikan Program Support Aktivasi" onClick={() => setTetapkan(JENIS.AKTIVASI)} testid="cb-prog-act-tetapkan-aktivasi" disabled={busy} />
                    <ActionBtn icon={Tags} label="Tetapkan: Support Cashback" onClick={() => setTetapkan(JENIS.CASHBACK)} testid="cb-prog-act-tetapkan-cashback" disabled={busy} />
                  </div>
                </div>
              )}
              <div className="pt-3 border-t border-slate-100" data-testid="cb-prog-selesai-bagian">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-2">Status Selesai (Approval PIC)</p>
                {/* `key` ber-versi: sesudah program diubah (edit, tetapkan jenis) panel
                    menghitung ulang ringkasannya, bukan menampilkan angka sebelum perubahan. */}
                <PanelSelesai key={`${program.id}-${program.versi_aturan || 0}`} program={program} cbBase={CB_BASE} onBerubah={muatUlangProgram} />
              </div>
            </div>
          )}

          {tab === "lampiran" && (
            <div className="space-y-3">
              <p className="text-sm text-slate-600">Lampirkan memo/surat price protection dari supplier sebagai bukti dasar program ini.</p>
              <button onClick={() => setShowAtt(true)} data-testid="cb-prog-open-attachments"
                      className="h-10 px-4 rounded-lg border border-emerald-200 text-emerald-700 hover:bg-emerald-50 text-sm font-medium inline-flex items-center gap-2">
                <Paperclip className="w-4 h-4" /> Kelola Lampiran (Memo Supplier)
              </button>
            </div>
          )}
        </div>
      </div>

      {showAtt && (
        <AttachmentPanel module="cashback_program" objId={program.id} objLabel={`${program.code} · ${program.nama_program}`}
                          onClose={() => setShowAtt(false)} />
      )}
      {tetapkan && (
        <TetapkanJenisModal program={program} jenis={tetapkan} cbBase={CB_BASE} onClose={() => setTetapkan(null)}
                            onSelesai={(item) => { setTetapkan(null); if (item) onChanged(item); else muatUlangProgram(); }} />
      )}
    </div>
  );
}

function InfoRow({ label, value, col2 }) {
  return (
    <div className={col2 ? "md:col-span-2" : ""}>
      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</p>
      <p className="text-sm text-slate-800 mt-0.5">{value}</p>
    </div>
  );
}

export function ActionBtn({ icon: Icon, label, onClick, testid, primary, tone, disabled }) {
  const cls = tone === "rose" ? "border-rose-200 text-rose-700 hover:bg-rose-50"
    : primary ? "bg-emerald-600 border-emerald-600 text-white hover:bg-emerald-700"
    : "border-slate-200 text-slate-700 hover:bg-slate-50";
  return (
    <button onClick={onClick} disabled={disabled} data-testid={testid}
            className={`h-9 px-3 rounded-lg border text-sm font-medium inline-flex items-center gap-1.5 disabled:opacity-50 ${cls}`}>
      <Icon className="w-4 h-4" /> {label}
    </button>
  );
}

// ============ Kandidat & Eligibility (brief butir 4-10) — DIEKSPOR supaya
// CashbackClaimDetail.jsx bisa memakai ULANG tabel yang sama untuk "Tambah
// IMEI" ke batch yang sudah ada, tanpa menyalin logika kandidat/impor. ============
// Brief hal. 5 & 9: "Untuk Produk yang Tidak eligible & Menunggu Aktivasi" —
// daftar yang sama dengan `routers/pp/pindah.py::STATUS_BOLEH_PINDAH`.
const STATUS_BOLEH_PINDAH = ["not_eligible", "pending_activation"];

const STATUS_TABS = [
  { key: "", label: "Semua" },
  { key: "eligible", label: "Eligible" },
  { key: "pending_activation", label: "Menunggu Aktivasi" },
  { key: "not_eligible", label: "Tidak Eligible" },
  { key: "already_claimed", label: "Sudah Diklaim" },
];

export function KandidatManager({ program, navigate, onClaimCreated }) {
  const { can } = useAuth();
  // Panel impor tanggal aktivasi memanggil `POST .../import-aktivasi`
  // (`finance_cashback_claim.update`). Dihitung DI SINI, bukan prop dari
  // pemanggil (keberatan tes-07): gerbang yang dioper sebagai prop tidak bisa
  // dibandingkan dengan endpointnya, dan pemakai ulang bisa mengoper nilai lain.
  const canUpdate = can("finance_cashback_claim", "update");
  // Aturan #3: tombol "Buat Claim Batch" memanggil `POST /cashback/claims` yang
  // di backend mensyaratkan `finance_cashback_claim.create` — dulu digerbangi
  // `canUpdate`, jadi pemegang update-tanpa-create melihat tombol yang ujungnya
  // 403, sedangkan pemegang create-tanpa-update tidak bisa membuat batch sama
  // sekali. Dihitung DI SINI (bukan prop) supaya pemakai ulang komponen ini
  // tidak bisa lupa mengopernya. Kolom checkbox ikut gerbang ini karena
  // satu-satunya gunanya memilih IMEI untuk batch. Panel impor aktivasi tetap
  // `canUpdate` (endpointnya `update`).
  const canCreate = can("finance_cashback_claim", "create");
  // Pindahkan (brief 22 Sep A8) memanggil `POST /cashback/pp/anggota/pindah`
  // yang mensyaratkan `finance_cashback_claim.update` — kunci yang SAMA di sini.
  // Hanya program jenis baru (keanggotaan tersimpan); program lama tidak punya
  // penempatan untuk dipindah.
  const canPindah = can("finance_cashback_claim", "update");
  const berjenis = program.mode_mesin === MODE_KEANGGOTAAN;
  // Program Selesai / menunggu PIC (PP4): impor, pindah, dan klaim baru ditolak
  // backend 409 — keputusan `terkunci_alasan` dikirim backend bersama programnya.
  const terkunci = !!program.terkunci_alasan;
  const [kandidat, setKandidat] = useState([]);
  const [ringkasan, setRingkasan] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [statusF, setStatusF] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [pilihPindah, setPilihPindah] = useState(() => new Set());
  const [showPindah, setShowPindah] = useState(false);
  const [showBuatClaim, setShowBuatClaim] = useState(false);
  const [reload, setReload] = useState(0);

  const load = async () => {
    setLoading(true); setLoadError(null);
    try {
      const r = await api.get(`${CB_BASE}/programs/${program.id}/kandidat`);
      setKandidat(r.data.items || []);
      setRingkasan(r.data.ringkasan || {});
    } catch (e) { setLoadError(e); toast.error(e?.response?.data?.detail || "Gagal memuat daftar kandidat IMEI"); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [program.id, reload]);

  const filteredRaw = useMemo(() => kandidat.filter((k) => !statusF || k.status === statusF), [kandidat, statusF]);
  // Eligibility dihitung LIVE dari SKU program ini saja (bukan query
  // ber-`to_list(N)` yang bisa terpotong) — aman disortir tanpa
  // BatasDaftarNotice. Checkbox pilih tetap mengacu ke `k.imei`, jadi
  // reorder tampilan tidak memengaruhi status terpilih.
  // Kolom tabel per halaman (brief hal. 3–4 & 7–8) dari SATU peta definisi
  // (`pp/halamanPP.js::KOLOM_UNIT`) — program lama tetap kolom hari ini (Modal
  // Asli dst.), program jenis baru tanpa Modal.
  const kunciKolom = kunciTampilan(program);
  const kolom = KOLOM_UNIT[kunciKolom];
  const berbagian = HALAMAN_BERBAGIAN.includes(kunciKolom);
  const kandidatSort = useSortState(null);
  const filtered = sortRows(filteredRaw, kandidatSort.sortKey, kandidatSort.sortDir, aksesUrut(kolom));
  // Cashback ZOA GROUP dibagi "Terjual" / "Belum Terjual" (brief hal. 7,
  // pertanyaan no. 20); urutan tampil tetap mengikuti urutan pilihan di atas.
  const kelompok = berbagian
    ? [BAGIAN.TERJUAL, BAGIAN.BELUM_TERJUAL].map((b) => ({ kunci: b, baris: filtered.filter((k) => (k.bagian || BAGIAN.TERJUAL) === b) }))
    : [{ kunci: "semua", baris: filtered }];
  const selectedItems = useMemo(() => kandidat.filter((k) => selected.has(k.imei)), [kandidat, selected]);
  const totalSelected = useMemo(() => round2(selectedItems.reduce((a, b) => a + b.cashback_nominal, 0)), [selectedItems]);

  const toggle = (imei, bisaDipilih) => {
    if (!bisaDipilih) return;
    setSelected((prev) => { const n = new Set(prev); n.has(imei) ? n.delete(imei) : n.add(imei); return n; });
  };
  const toggleAllEligible = () => {
    const eligibleImeis = filtered.filter((k) => k.status === "eligible").map((k) => k.imei);
    const semuaTerpilih = eligibleImeis.length > 0 && eligibleImeis.every((i) => selected.has(i));
    setSelected((prev) => {
      const n = new Set(prev);
      eligibleImeis.forEach((i) => semuaTerpilih ? n.delete(i) : n.add(i));
      return n;
    });
  };

  // Brief hal. 5: unit Tidak Eligible & Menunggu Aktivasi bisa dipilih lalu
  // dipindah. Anggota siklus lama tidak ditawarkan (backend juga menolaknya).
  const bisaDipindah = (k) => berjenis && !!k.anggota_id && !k.siklus_lama && STATUS_BOLEH_PINDAH.includes(k.status);
  const togglePindah = (id) => {
    setPilihPindah((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };
  const kolomPindah = canPindah && berjenis && !terkunci;

  // Rekap per program (brief hal. 5 & 8) — izin lihat halaman ini cukup.
  const unduhRekap = () => downloadBlob(`${CB_BASE}/programs/${program.id}/rekap-xlsx`, `rekap-${program.code}.xlsx`,
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet")
    .catch(() => toast.error("Gagal mengunduh rekap"));

  const eligibleCount = filtered.filter((k) => k.status === "eligible").length;

  return (
    <div className="space-y-4">
      {terkunci && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-2.5 text-xs text-indigo-900 flex items-start gap-2" data-testid="cb-kandidat-terkunci-info">
          <Lock className="w-4 h-4 mt-0.5 shrink-0" /> <span>{program.terkunci_alasan}</span>
        </div>
      )}
      {canUpdate && !terkunci && (
        <PanelImporAktivasi program={program} cbBase={CB_BASE} unduhBerkas={downloadBlob}
                            onSelesai={() => setReload((n) => n + 1)} />
      )}

      <div className="flex items-center justify-end">
        <button onClick={unduhRekap} data-testid="cb-prog-rekap-xlsx"
                className="h-8 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium inline-flex items-center gap-1.5">
          <FileSpreadsheet className="w-3.5 h-3.5" /> Download Rekap .xlsx
        </button>
      </div>

      <div className="flex items-center gap-1.5 flex-wrap">
        {STATUS_TABS.map((t) => {
          const r = t.key ? ringkasan[t.key] : null;
          return (
            <button key={t.key || "all"} onClick={() => setStatusF(t.key)} data-testid={`cb-kandidat-filter-${t.key || "semua"}`}
                    className={`h-9 px-3 rounded-lg border text-xs font-medium inline-flex items-center gap-1.5 ${statusF === t.key ? "bg-slate-900 border-slate-900 text-white" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
              {t.label}{r ? ` (${r.count} · ${idr(r.total_cashback)})` : ""}
            </button>
          );
        })}
      </div>

      <div className="border border-slate-200 rounded-lg overflow-x-auto max-h-[420px] overflow-y-auto" data-testid="cb-kandidat-tabel" data-kolom={kunciKolom}>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[11px] uppercase text-slate-500 sticky top-0">
            <tr>
              {kolomPindah && <th className="px-2 py-2 text-center" title="Pilih unit Tidak Eligible / Menunggu Aktivasi untuk dipindah">Pindah</th>}
              {canCreate && (
                <th className="px-2 py-2 text-center">
                  <input type="checkbox" data-testid="cb-kandidat-select-all" onChange={toggleAllEligible}
                         checked={eligibleCount > 0 && filtered.filter((k) => k.status === "eligible").every((k) => selected.has(k.imei))}
                         disabled={eligibleCount === 0} />
                </th>
              )}
              {kolom.map((c) => (c.urut ? (
                <SortTh key={c.kunci} label={c.label} sortKeyName={c.kunci} sortKey={kandidatSort.sortKey} sortDir={kandidatSort.sortDir}
                        onSort={kandidatSort.toggle} align={rataKolom(c) === "right" ? "right" : "left"} testid={`cb-kandidat-sort-${c.tid}`} />
              ) : (
                <th key={c.kunci} className={`px-2 py-2 ${kelasRata(c)} ${c.format === "alasan" ? "min-w-[220px]" : ""}`} title={c.judul}>{c.label}</th>
              )))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            <LoadingState loading={loading} error={loadError} onRetry={load} kosong={!loading && !loadError && filtered.length === 0}
                          pesanKosong="Tidak ada IMEI kandidat pada filter ini" varian="tabel" kolom={kolom.length + (canCreate ? 1 : 0) + (kolomPindah ? 1 : 0)} data-testid="cb-kandidat-loading">
              {kelompok.map((g) => (
                <Fragment key={g.kunci}>
                  {berbagian && (
                    <tr className="bg-slate-50/80" data-testid={`cb-kandidat-bagian-${g.kunci}`}>
                      <td colSpan={kolom.length + (canCreate ? 1 : 0) + (kolomPindah ? 1 : 0)} className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-600">
                        {LABEL_BAGIAN[g.kunci]} · {g.baris.length} unit
                      </td>
                    </tr>
                  )}
                  {g.baris.map((k) => {
                    const bisaDipilih = k.status === "eligible";
                    return (
                      <tr key={k.anggota_id || k.imei} data-testid={`cb-kandidat-row-${k.imei}`} className={selected.has(k.imei) ? "bg-emerald-50/50" : ""}>
                        {kolomPindah && (
                          <td className="px-2 py-2 text-center">
                            <input type="checkbox" data-testid={`cb-pindah-cb-${k.imei}`} checked={pilihPindah.has(k.anggota_id)}
                                   disabled={!bisaDipindah(k)} onChange={() => togglePindah(k.anggota_id)}
                                   title={!bisaDipindah(k) ? "Hanya unit Tidak Eligible / Menunggu Aktivasi yang bisa dipindah" : undefined} />
                          </td>
                        )}
                        {canCreate && (
                          <td className="px-2 py-2 text-center">
                            <input type="checkbox" data-testid={`cb-kandidat-cb-${k.imei}`} checked={selected.has(k.imei)}
                                   disabled={!bisaDipilih} onChange={() => toggle(k.imei, bisaDipilih)} title={!bisaDipilih ? "Hanya IMEI berstatus Eligible yang bisa dipilih" : undefined} />
                          </td>
                        )}
                        {kolom.map((c) => (
                          <td key={c.kunci} className={`px-2 py-2 ${kelasRata(c)}`}><SelKolom kolom={c} baris={k} Status={EligibilityPill} /></td>
                        ))}
                      </tr>
                    );
                  })}
                </Fragment>
              ))}
            </LoadingState>
          </tbody>
        </table>
      </div>

      {canPindah && berjenis && !terkunci && (
        <div className="flex items-center justify-between flex-wrap gap-2 bg-slate-50 border border-slate-200 rounded-lg px-4 py-3">
          <p className="text-sm text-slate-700"><span className="font-semibold">{pilihPindah.size} unit</span> dipilih untuk dipindah ke program periode lain</p>
          <button onClick={() => setShowPindah(true)} disabled={pilihPindah.size === 0} data-testid="cb-pindah-btn"
                  className="h-9 px-4 rounded-lg border border-slate-300 bg-white hover:bg-slate-100 text-slate-800 text-sm font-medium disabled:opacity-50 inline-flex items-center gap-1.5">
            <ArrowRightLeft className="w-4 h-4" /> Pindahkan
          </button>
        </div>
      )}

      {showPindah && (
        <PindahUnitModal anggotaIds={[...pilihPindah]} cbBase={CB_BASE} onClose={() => setShowPindah(false)}
                         onDipindah={() => { setShowPindah(false); setPilihPindah(new Set()); setReload((n) => n + 1); }} />
      )}

      {/* Kunci Selesai di DALAM gerbang izin (ternary, bukan `&&` kedua): gerbang
          `canCreate` tetap menjadi syarat `&&` terdekat tombol ini. */}
      {canCreate && (terkunci ? null : (
        <div className="flex items-center justify-between flex-wrap gap-2 bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3">
          <p className="text-sm text-emerald-800">
            <span className="font-semibold">{selected.size} IMEI</span> terpilih · Potensi <span className="font-semibold">{idr(totalSelected)}</span>
          </p>
          <button onClick={() => setShowBuatClaim(true)} disabled={selected.size === 0} data-testid="cb-buat-claim-btn"
                  className="h-9 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50 inline-flex items-center gap-1.5">
            <PlusCircle className="w-4 h-4" /> Buat Claim Batch
          </button>
        </div>
      ))}

      {showBuatClaim && (
        <BuatClaimModal program={program} selectedItems={selectedItems} totalSelected={totalSelected}
                        onClose={() => setShowBuatClaim(false)}
                        onCreated={(batch) => {
                          setShowBuatClaim(false); setSelected(new Set());
                          // Permintaan bos 21 Agu E1: klaim di luar periode klaim TETAP tersimpan
                          // — toast MERAH (bukan gagal) supaya staf langsung sadar sebelum lanjut
                          // ke halaman detail (yang juga menampilkan panel & pill peringatan sama).
                          if (batch.di_luar_periode_klaim) {
                            toast.error(`Claim Batch ${batch.code} dibuat, TAPI di luar periode klaim — ${batch.di_luar_periode_klaim_alasan || ""}`);
                          } else {
                            toast.success(`Claim Batch ${batch.code} dibuat ✓`);
                          }
                          onClaimCreated?.(); navigate?.(`/finance/cashback/claims/${batch.id}`);
                        }} />
      )}
    </div>
  );
}

function round2(n) { return Math.round((n + Number.EPSILON) * 100) / 100; }

function BuatClaimModal({ program, selectedItems, totalSelected, onClose, onCreated }) {
  const [keterangan, setKeterangan] = useState("");
  const [busy, setBusy] = useState(false);
  // Peringatan PRA-SIMPAN (murni perbandingan tanggal di klien, sama seperti
  // `lewatDeadline` di SubmitModal) — klaim ini akan dibuat HARI INI, jadi
  // dibandingkan langsung terhadap periode_klaim_dari/sampai program. Backend
  // TETAP jadi sumber kebenaran (`_cek_periode_klaim`); ini cuma bantu staf
  // sadar SEBELUM menekan submit, bukan pengganti validasi server.
  const hariIni = hariIniISO();
  const diLuarPeriodeKlaim = !!(
    (program.periode_klaim_dari && hariIni < program.periode_klaim_dari) ||
    (program.periode_klaim_sampai && hariIni > program.periode_klaim_sampai)
  );
  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post(`${CB_BASE}/claims`, { program_id: program.id, imeis: selectedItems.map((k) => k.imei), keterangan });
      onCreated(r.data.item);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal membuat Claim Batch"); }
    finally { setBusy(false); }
  };
  return (
    <Modal title="Buat Claim Batch" icon={PlusCircle} onClose={onClose} onSubmit={submit} submitLabel="Buat Claim Batch"
           busy={busy} testid="cb-buat-claim-modal">
      <p className="text-sm text-slate-600">{selectedItems.length} IMEI · Total potensi cashback <b>{idr(totalSelected)}</b></p>
      <p className="text-xs text-slate-500">Supplier: {program.supplier_nama}</p>
      {diLuarPeriodeKlaim && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 inline-flex items-start gap-2" data-testid="cb-buat-claim-warn-periode">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> Hari ini ({formatTanggal(hariIni)}) di LUAR periode klaim program
          ({program.periode_klaim_dari ? formatTanggal(program.periode_klaim_dari) : "-"} s.d. {program.periode_klaim_sampai ? formatTanggal(program.periode_klaim_sampai) : "-"})
          — klaim tetap bisa dibuat, tapi supplier berhak menolak karena di luar periode.
        </div>
      )}
      <div className="max-h-40 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100">
        {selectedItems.map((k) => (
          <div key={k.imei} className="px-3 py-1.5 flex items-center justify-between text-xs">
            <span className="font-mono">{k.imei}</span><span className="text-slate-500">{k.sku}</span><span className="font-mono">{idr(k.cashback_nominal)}</span>
          </div>
        ))}
      </div>
      <Field label="Keterangan (opsional)">
        <textarea rows={2} value={keterangan} onChange={(e) => setKeterangan(e.target.value)} data-testid="cb-buat-claim-ket" className={inputCls + " h-auto py-2"} />
      </Field>
    </Modal>
  );
}
